import * as logger from "../utils/logger";
import Api from "../services/api";
import { CoachingActions } from "../modules/coaching/store/actions";
import { CoachingNotificationService } from "../modules/coaching/services/coachingNotificationService";
import { CoachingSessionService } from "../modules/coaching/services/coachingSessionService";
import { DriveStore } from "../store/reducers";
import { ICoachingPendingCompletion } from "../modules/coaching/store/types";
import { NumberOfSyncRetries } from "./base";
import { isDuplicateException, isInvalidPermissionsException } from "../utils/errorHandler/error.types";
import { COACHING_SYNC_LOG_PREFIX } from "../modules/coaching/constants";
import { isApiError, isTerminalError } from "./coachingSyncErrors";

/** Stage tag appended to the shared root so the whole flow greps by `[CoachingSync]`. */
const LOG_PREFIX = `${ COACHING_SYNC_LOG_PREFIX } [Completion]`;

/**
 * Completes a session once its comments have settled and records the result. The orchestrator
 * (CoachingSync) decides when to call this; this class only performs the completion and knows
 * its retry/fail policy.
 */
export class CoachingCompletionSync {
    constructor(
        private readonly api: Api,
        private readonly store: DriveStore,
        private readonly sessionService: CoachingSessionService,
        private readonly notifications: CoachingNotificationService
    ) {}

    /**
     * Acknowledge the session (the server auto-transitions it to Completed) and record the result:
     *   - synced / duplicate  → marked synced
     *   - no permission       → fail and notify the driver
     *   - terminal            → fail
     *   - anything else       → default: treat as transient and retry on a bounded budget, failing
     *                           only once the budget is exhausted
     */
    async push(userId: string, sessionId: string, completion: ICoachingPendingCompletion): Promise<void> {
        logger.log(
            logger.Source.SYNC,
            `${ LOG_PREFIX } pushing session ${ sessionId } ` +
                `(ack ${ completion.requestedDateTime }, retries left ${ completion._syncCounter ?? NumberOfSyncRetries })`,
            this.api.getUser()
        );
        try {
            await this.sessionService.completeSession(sessionId, completion.requestedDateTime);
            logger.log(
                logger.Source.SYNC,
                `${ LOG_PREFIX } session ${ sessionId } acknowledged — marked synced`,
                this.api.getUser()
            );
            this.markSynced(userId, sessionId);
        } catch (error) {
            if (!isApiError(error)) {
                // An unexpected runtime fault, not a server verdict — fail the completion and let
                // the exception surface rather than silently retrying a bug.
                logger.error(
                    logger.Source.SYNC,
                    `${ LOG_PREFIX } session ${ sessionId } failed with an unexpected error: ` +
                        `${ error instanceof Error ? error.message : String(error) }`,
                    this.api.getUser()
                );
                this.drop(userId, sessionId, "syncFailed");
                throw error;
            }
            const apiError = error;

            if (isDuplicateException(apiError)) {
                logger.log(
                    logger.Source.SYNC,
                    `${ LOG_PREFIX } session ${ sessionId } already completed (duplicate) — marked synced`,
                    this.api.getUser()
                );
                this.markSynced(userId, sessionId);
                return;
            }

            if (isInvalidPermissionsException(apiError)) {
                logger.error(
                    logger.Source.SYNC,
                    `${ LOG_PREFIX } session ${ sessionId } rejected — no permission`,
                    this.api.getUser(),
                    apiError
                );
                this.notifications.notifySessionCompletionDroppedDueToNoPermission(userId, sessionId);
                this.drop(userId, sessionId, "syncFailed");
                return;
            }

            if (isTerminalError(apiError)) {
                logger.error(
                    logger.Source.SYNC,
                    `${ LOG_PREFIX } session ${ sessionId } rejected permanently: ${ apiError.message }`,
                    this.api.getUser(),
                    apiError
                );
                this.drop(userId, sessionId, "syncFailed");
                return;
            }

            const remaining = (completion._syncCounter ?? NumberOfSyncRetries) - 1;
            if (remaining <= 0) {
                logger.error(
                    logger.Source.SYNC,
                    `${ LOG_PREFIX } session ${ sessionId } exhausted its retry budget — failing: ${ apiError.message }`,
                    this.api.getUser(),
                    apiError
                );
                this.drop(userId, sessionId, "syncFailed");
                return;
            }
            logger.log(
                logger.Source.SYNC,
                `${ LOG_PREFIX } session ${ sessionId } hit a transient error (${ apiError.message }); ` +
                    `${ remaining } retries left`,
                this.api.getUser()
            );
            this.store.dispatch(
                CoachingActions.updateCoachingCompletionState({
                    userId,
                    sessionId,
                    patch: { _syncCounter: remaining }
                })
            );
        }
    }

    /** Fail the completion because the session can no longer accept it (finalized/obsolete/unusable). */
    drop(userId: string, sessionId: string, failureReason: string): void {
        this.store.dispatch(
            CoachingActions.updateCoachingCompletionState({
                userId,
                sessionId,
                patch: {
                    modified: undefined,
                    syncStatus: "failed",
                    failureReason
                }
            })
        );
    }

    private markSynced(userId: string, sessionId: string): void {
        this.store.dispatch(
            CoachingActions.updateCoachingCompletionState({
                userId,
                sessionId,
                patch: {
                    modified: undefined,
                    syncStatus: "synced",
                    failureReason: undefined,
                    _syncCounter: undefined
                }
            })
        );
    }
}
